import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import WSDataGrid, { IColumns } from "../../../Components/DataGrid/WSDataGrid";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { callAPI, getLocalValue, LoacalVariables, Method } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";

const CompletedLoanList: React.FC<any> = React.memo((props) => {
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();

    const [districtOptions, setDistrictOptions] = useState<any[]>([]);
    const [blockOptions, setBlockOptions] = useState<any[]>([]);
    const [cdsOptions, setCdsOptions] = useState<any[]>([]);
    const userType = getLocalValue(LoacalVariables.UserType);
    breadCrumbsStore.updateBreadCrumbs([{ "Completed Loans": "/loan/requests/list" }]);

     useEffect(() => {
            if (userType === "admin") {
                fetchDistricts();
                fetchBlocks();
                fetchCDS();
            } else if (userType === "district") {
                const districtId = getLocalValue(LoacalVariables.District);
                if (districtId) {
                    fetchBlocks(districtId);
                }
            } else if (userType === 'block') {
                fetchCDS();
            }
        }, []);
    
        const fetchDistricts = async () => {
            try {
                const res = await callAPI("districts/getAll", Method.GET);
                setDistrictOptions(
                    res?.data?.map((d: any) => ({ label: d.name, value: d._id })) || []
                );
            } catch (error) {
                console.error("Failed to fetch districts:", error);
                showMessage("Failed to load districts", "error");
            }
        };
    
        const fetchBlocks = async (districtId?: string) => {
            try {
                // setLoadingRegion(true);
                // const res = await callAPI(`blocks/byDistrict/${districtId}`, Method.GET);
                const res = await callAPI('blocks/getAll', Method.GET);
    
                setBlockOptions(
                    res?.data?.map((b: any) => ({ label: b.name, value: b._id })) || []
                );
            } catch (error) {
                console.error("Failed to fetch blocks:", error);
                showMessage("Failed to load blocks", "error");
            } finally {
                // setLoadingRegion(false);
            }
        };
    
        const fetchCDS = async (blockId?: string) => {
            try {
                // const res = await callAPI(`cds/byBlock/${blockId}`, Method.GET);
                const res = await callAPI('cds/getAll', Method.GET);
    
    
                setCdsOptions(
                    res?.data.map((c: any) => ({ label: c.name, value: c._id })) || []
                );
            } catch (error) {
                console.error("Failed to fetch CDS:", error);
                showMessage("Failed to load CDS", "error");
            }
        };
    

    const source = {
        url: "loans/search",
        page: 1,
        pageSize: 10,
        filter: [
            {
                field: "status",
                operator: "eq",
                value: "completed"
            }
        ],
        sort: [{ field: "DueDate", direction: "desc" }]
    };

    let limitedActions;

    switch (userType) {
        case 'admin':
        case 'district':
            limitedActions = ["View", "Activity Logs", "View Repayments"];
            break;
        case 'block':
            limitedActions = ["View", "View Repayments"];
            break;
        case 'cds':
            limitedActions = ["View", "View Repayments"];
            break;
        default:
            limitedActions = ["View"];
    }

    //edit / delete or perform other actions.
    const handleAction = async (action: string, row: any) => {
        if (action === "Activity Logs") {
            navigate(`/loan/activityLogs/${row?._id}`)
        } else if (action === "View") {
            navigate(`/loan/detail/${row?._id}`)
        }
        else if (action === "View Repayments") {
            navigate(`/loan/repayment/requests/list/filter/${row?._id}`)
        }
    };

    const columns: IColumns[] = [
        {
            header: "Loan ID",
            fieldName: "loanId",
            tooltip: true,
            sortable: true
        },

        {
            header: "Enterprise Name",
            fieldName: "enterpriseName",
            tooltip: true,
            sortable: true
        },


        {
            header: "Disbursed Amount",
            fieldName: "sanctionedCefAmount",
            tooltip: true,
            sortable: true,
            type: "currency"
        },

        {
            header: userType === 'district' || userType === "admin" ? "Block" : "CDS",
            fieldName: userType === 'district' || userType === "admin" ? "block.name" : "cds.name",
            tooltip: true,
            type: "default"
        },

        {
            header: "Group Loan",
            fieldName: "isGroupLoan", // assuming the field is 'isGroupLoan'
            tooltip: true,
            sortable: true,
            type: 'default',
            render: (row: any) => row.isGroupLoan ? "Yes" : "No",  // converting true/false to Yes/No
        },

        {
            header: "Status",
            fieldName: "status",
            tooltip: true,
            type: "status"
        },

        {
            header: "Date",
            fieldName: "completedDate",
            type: "date",
        },

        {
            header: "Actions",
            fieldName: "actions",
            type: "action",
            actions: limitedActions,
            actionCallback: (action, row) => handleAction(action, row)
        }
    ];


    const filterFields = [
        { type: "text", fieldName: "loanId", label: "Loan ID" },
        { type: "text", fieldName: "applicantName", label: "Applicant Name" },
        { type: "text", fieldName: "secondApplicantName", label: "Second Applicant Name" },
        { type: "text", fieldName: "mobile", label: "Mobile Number" },
        { type: "text", fieldName: "whatsappNumber", label: "WhatsApp Number" },
        { type: "text", fieldName: "enterpriseName", label: "Enterprise Name" },
        { type: "text", fieldName: "referenceNumber", label: "Reference Number" },

        // { type: "dropdown", fieldName: "loanType", label: "Loan Type", options: loanTypeOptions },
        // { type: "dropdown", fieldName: "district", label: "District", options: districtOptions },
        userType == 'district' && { type: "dropdown", fieldName: "block", label: "Block", options: blockOptions },
        { type: "dropdown", fieldName: "cds", label: "CDS", options: cdsOptions },

        { type: "date", fieldName: "createdAt", label: "Created Date" },
        { type: "date", fieldName: "fundTransferDate", label: "Disbursed Date" },
        { type: "date", fieldName: "sanctionedDate", label: "Sanctioned Date" },
        { type: "date", fieldName: "completedDate", label: "Completed Date" }
    ];


    return (
        <>
            <Helmet>
                <title>Loans | Willsoar Technologies</title>
                <meta
                    name="description"
                    content="Manage all loans in one place. View, add, or edit loan information easily."
                />
            </Helmet>

            <Grid p={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} mb={6}>
                        <WSDataGrid
                            id="completeLoanGrid"
                            title="Completed Loans"
                            columns={columns}
                            filterFields={filterFields}
                            filters={[{ field: "status", operator: "eq", value: 'completed' }]}
                            searchBox={{ fieldName: "mobile", type: "number" }}
                            placeholder={"Search by Mobile Number"}
                            source={source}
                            gridHeaderSx={{
                                backgroundColor: "whitesmoke",
                                border: "1px solid whitesmoke"
                            }}
                            exportCsv
                            buttonStyle="Sigma"
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
});


export default CompletedLoanList;